import React, { useEffect } from "react";
import { ConfigProvider, message } from "antd";
import { SWRConfig } from "swr";
import usePersistentState from "./App/AdminPortal/hooks/usePersistentState";
import UserContext from "./App/Contexts/UserContext";
import { fetcher } from "./App/AdminPortal/utils/fetcher";
import { theme } from "./App/AdminPortal/utils/theme";
import initAxiosGlobalConfigs, { setAxiosInterceptors } from "./App/API/axios.Config";
import { PublicClientApplication } from "@azure/msal-browser";
import { getMsalConfig } from "./authConfig";
import "./App.css";
import laggy from "./App/middlewares/laggy";
import { MsalProvider, useMsal } from "@azure/msal-react";
import { getRoleFromAuth, getUserType } from './utils/commonUtils';
import { RoutesLookup } from './App/Routes/RoutesLookup';
import { DEFAULT_USER_TYPE, USER_TYPES } from './App/Constants/ApplicationConstants';
import Router from './App/Routes/Router';
import { CartProvider } from '../src/App/Contexts/CartContext';
import { doLogout } from './utils/manageUserData';

const getMsalInstance = (configType) => {
  const msalConfig = getMsalConfig(configType);
  return new PublicClientApplication(msalConfig);
};

const MsalWrapperHOC = React.memo(({ configType, children }) => {
  const msalInstance = React.useMemo(
    () => getMsalInstance(configType),
    [configType]
  );

  if (!msalInstance) {
    console.error('Failed to initialize MSAL instance');
    return null;
  }

  return <MsalProvider instance={msalInstance}>{children}</MsalProvider>;
});

const AppContainer = () => {
  const { instance } = useMsal();
  const [auth, setAuth] = usePersistentState("auth", {
    initialValue: {},
    isSession: false
  });
  useEffect(()=>{message.config({top:100})})
  const userType = getUserType(getRoleFromAuth(auth))

  const configType = React.useMemo(() => {
    if (window.location.pathname === RoutesLookup.ADMIN_LOGIN || userType === USER_TYPES.ADMIN) {
      return USER_TYPES.ADMIN
    } else if (window.location.pathname === RoutesLookup.AGENT_LOGIN || userType === USER_TYPES.AGENT) {
      return USER_TYPES.AGENT
    }
    return DEFAULT_USER_TYPE
  }, [userType]);

  // Add window focus event handler for SSO logout sync
  useEffect(() => {
    const handleWindowFocus = async () => {
      debugger;
      if (auth?.token && instance) {
        try {
          // Try to get a token silently to check if user is still authenticated
          const account = instance.getActiveAccount();
          if (account) {
            debugger;
            await instance.ssoSilent({
              scopes: ["openid", "profile"],
              loginHint: account.username,
            });
          }
        } catch (error) {
          debugger;
          // If silent SSO fails, user has been logged out elsewhere
          if (error.errorMessage?.includes("interaction_required") || 
              error.errorMessage?.includes("login_required")) {
            console.log("User has been logged out elsewhere. Logging out locally...");
            doLogout(instance, setAuth);
          }
        }
      }
    };
debugger;
    window.addEventListener('focus', handleWindowFocus);
    return () => {
      window.removeEventListener('focus', handleWindowFocus);
    };
  }, [auth, instance, setAuth]);

  useEffect(() => {
    if (auth?.token && instance) {
      initAxiosGlobalConfigs(auth);
      setAxiosInterceptors(instance);
    }
  }, [auth, instance]);

  useEffect(() => {
    if (!auth?.token) {
      localStorage.setItem("auth", JSON.stringify({ data: "{}" }));
    }
  }, [auth]);

  const ConditionalCartProvider = (props) => {
    const { children } = props
    return userType === USER_TYPES.ADMIN ? (
      <>{children}</>
    ) : (
      <CartProvider>{children}</CartProvider>
    );
  };

  return (
    <SWRConfig
      value={{
        fetcher: fetcher,
        onError: (error, key) => {
          if (error) {
            // TODO:  Add Proper error handling in this middleware
            message.error("Internal server Error!", error);
          }
        },
        use: [laggy],
      }}
    >
      <UserContext.Provider value={{ auth, setAuth, instance }}>
        <ConfigProvider theme={theme}>
          <MsalWrapperHOC configType={configType}>
            <ConditionalCartProvider>
              <Router />
            </ConditionalCartProvider>
          </MsalWrapperHOC>
        </ConfigProvider>
      </UserContext.Provider>
    </SWRConfig>
  );
};

export default AppContainer;
